<template>
  <div class="life-charts">
    <div class="chart-scroll" ref="scroll">
      <div class="chart-row">
      <div class="chart-inner" :style="{ width: innerWidth }">
        <div class="chart-card">
          <div class="chart-head">
            <section v-if="poolLeaders.length" class="pool-observe">
              <header class="pool-observe__head">
                <b>观察池</b>
                <button type="button" @click="poolDrawerOpen = !poolDrawerOpen">{{ poolDrawerOpen ? '收起' : '展开' }}</button>
              </header>
              <div v-show="poolDrawerOpen" class="pool-leader-band">
              <LeaderColumn
                v-for="(leader, idx) in poolLeaders"
                :key="leader.leaderId || leader.code"
                :leader="leader"
                :rank="idx + 1"
                :selected="!!poolPicked[String(leader.code || '').split('.')[0]]"
                checkable
                :show-catchup="false"
                brief-status
                @select="onPoolLeaderSelect"
              />
              </div>
            </section>
            <div class="chart-thumb-row">
              <button type="button" class="cycle-thumb is-on" @click="$emit('show-cycle')">
                <b>周期总图</b>
                <img v-if="cycleShot" :src="cycleShot" alt="周期总图">
              </button>
              <kingdom-thumb
                v-for="card in kingdomCards"
                :key="card.code"
                compact
                :title="card.title"
                :dates="card.dates"
                :series="card.series"
                :tags="card.tags"
                :picked="poolPicked"
                @pick="$emit('toggle-pool', $event)"
                @remove="$emit('delete-kingdom', card.code)"
                @open="$emit('open-kingdom', card.code)"
              />
              <aside v-if="pathMode === 'wave'" class="day-expand day-expand--band">
                <div class="day-expand__title">当日扩张详情</div>
                <ol>
                  <li
                    v-for="(row, index) in dayExpand"
                    :key="row.code"
                    :class="{ 'is-on': row.code === activeCode }"
                    @click="$emit('select-stock', row.code)"
                  >
                    <span class="day-expand__no">{{ index + 1 }}</span>
                    <i :style="{ background: row.color }" />
                    <span class="day-expand__name">{{ row.name }}</span>
                    <b :class="expandClass(row.ret)">{{ expandText(row.ret) }}</b>
                  </li>
                </ol>
                <div v-if="!dayExpand.length" class="day-expand__empty">暂无当日涨幅</div>
              </aside>
            </div>
            <div class="chart-head-main">
            <div class="chart-title">
              生命周期累计涨幅路径
              <el-tooltip content="纵轴是相对各自 D0 的累计涨幅。阶段色块来自演示配置。" placement="top">
                <i class="el-icon-question" />
              </el-tooltip>
            </div>
            <div class="rank-legend">
              <button
                v-for="(item, index) in legendItems"
                :key="'ret-' + item.name"
                type="button"
                :class="{ 'is-off': hiddenNames[item.name] }"
                @click="toggleLegend(item.name)"
              >
                <span class="rank-legend__no">{{ index + 1 }}</span>
                <i :style="{ background: item.color }" />
                <span>{{ item.name }}</span>
              </button>
            </div>
            </div>
          </div>
          <div class="chart-plot">
            <div ref="returnEl" class="chart chart--return" :class="{ 'is-fixed': dateSlot > 0 }" />
            <aside class="burst-stat">
              <div class="burst-stat__title">今日爆量 {{ burstStatDate }}</div>
              <ol>
                <li
                  v-for="(row, index) in burstStatRows"
                  :key="row.code + row.date + row.eventName"
                  :class="{ 'is-on': row.code === activeCode }"
                  @click="openBurstStat(row)"
                >
                  <span class="burst-stat__no">{{ index + 1 }}</span>
                  <i :style="{ background: row.color }" />
                  <span class="burst-stat__name">{{ row.name }}</span>
                  <span class="burst-stat__tag" :style="{ color: row.color }">{{ row.eventName === '高位爆量' ? '高位' : '爆量' }}</span>
                  <b :class="expandClass(row.ret)">{{ expandText(row.ret) }}</b>
                </li>
              </ol>
              <div v-if="!burstStatRows.length" class="burst-stat__empty">今日暂无爆量</div>
            </aside>
          </div>
        </div>
        <div v-if="dateSlot > 0" class="chart-card finals-axis">
          <div class="finals-axis__title">首板终局</div>
          <div ref="finalsTrack" class="finals-axis__track" :style="{ height: finalsTrackHeight }">
            <div
              v-for="(date, index) in returnLabels"
              :key="'final-' + date"
              ref="finalsCols"
              class="finals-col"
              :style="{ width: dateSlot + 'px', left: (finalsLeft[index] || 0) + 'px' }"
            >
              <div class="finals-date">{{ shortDate(date) }}</div>
              <div
                v-for="stock in finalsOn(date)"
                :key="stock.code"
                class="finals-line"
                :class="{ 'is-hit': isConceptHit(stock) }"
                :style="hitStyle(stock)"
              >
                <b>{{ stock.name }}</b> {{ stock.score }}//{{ stock.cap }}//<span class="finals-star">★{{ stock.star }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="chart-card">
          <div class="chart-head">
            <div class="chart-title">
              生命周期连板高度演化
              <el-tooltip content="每天的最高连板个股，从首板连到它的最高板。当天最高只有 3 板时，改接当天第一个涨停。点击可看次日是否 +10% 开。" placement="top">
                <i class="el-icon-question" />
              </el-tooltip>
            </div>
            <div class="rank-legend">
              <button
                v-for="(item, index) in heightLegendItems"
                :key="'h-' + item.code"
                type="button"
                :class="{ 'is-off': hiddenNames[item.name] }"
                @click="toggleLegend(item.name)"
              >
                <span class="rank-legend__no">{{ index + 1 }}</span>
                <i :style="{ background: item.color }" />
                <span>{{ item.name }}</span>
              </button>
            </div>
          </div>
          <div ref="heightEl" class="chart chart--height" :class="{ 'is-fixed': dateSlot > 0 }" />
        </div>
      </div>
    </div>
    </div>
    <div v-if="activeEvent" class="event-pop">
      <div class="event-pop__head">
        <b>{{ activeEvent.stockName }}</b>
        <i class="el-icon-close" @click="activeEvent = null" />
      </div>
      <template v-if="activeEvent.events && activeEvent.events.length">
        <div class="event-pop__type">{{ activeEvent.tradeDate }} · {{ activeEvent.events.length }} 个事件</div>
        <button
          v-for="(item, index) in activeEvent.events"
          :key="index"
          type="button"
          class="event-pop__item"
          @click="openVolumeEvent(item)"
        >
          <i :style="{ background: item.color }" />
          <span>{{ item.stockName }} · {{ item.eventName }}</span>
        </button>
      </template>
      <template v-else>
        <div class="event-pop__type" :style="{ color: activeEvent.color }">{{ activeEvent.eventName }}</div>
        <div v-if="activeEvent.tradeDate">日期：{{ activeEvent.tradeDate }}</div>
        <div v-if="activeEvent.boardHeight != null && activeEvent.boardHeight !== ''">当时连板高度：{{ activeEvent.boardHeight }}B</div>
        <div v-if="activeEvent.nextDayRepairStatus">次日反馈：{{ activeEvent.nextDayRepairStatus }}</div>
        <div v-if="activeEvent.eventDescription" class="event-pop__desc">{{ activeEvent.eventDescription }}</div>
        <button v-if="activeEvent.backTo" type="button" class="event-pop__back" @click="activeEvent = activeEvent.backTo">返回列表</button>
      </template>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import KingdomThumb from './KingdomThumb.vue'
import LeaderColumn from '@/views/LeaderCommandCenter/LeaderColumn.vue'
import { EVENTS, applyScenario, dayLabels, eventMeta } from '@/views/CycleLifecycle/mockData'

export default {
  name: 'LifecycleCharts',
  components: { KingdomThumb, LeaderColumn },
  props: {
    stocks: { type: Array, default: () => [] },
    lastDay: { type: Number, default: 15 },
    rangeKey: { type: String, default: 'd15' },
    sourceId: { type: String, default: 'firstBoard' },
    showEvents: { type: Boolean, default: true },
    showBoardLabels: { type: Boolean, default: true },
    activeCode: { type: String, default: '' },
    activeDay: { type: Number, default: -1 },
    axisLabels: { type: Array, default: () => [] },
    waveAxisLabels: { type: Array, default: () => [] },
    pathMode: { type: String, default: 'straight' },
    dayMarks: { type: Object, default: () => ({}) },
    boardStocks: { type: Array, default: () => [] },
    dateSlot: { type: Number, default: 0 },
    finalsByDate: { type: Object, default: () => ({}) },
    hitCodes: { type: Object, default: () => ({}) },
    hitColor: { type: String, default: '' },
    dayExpand: { type: Array, default: () => [] },
    volumeMarks: { type: Object, default: () => ({}) },
    tradeDate: { type: String, default: '' },
    kingdomCards: { type: Array, default: () => [] },
    poolLeaders: { type: Array, default: () => [] },
    poolPicked: { type: Object, default: () => ({}) },
    showPoolMatch: { type: Boolean, default: false }
  },
  data() {
    return {
      returnChart: null,
      heightChart: null,
      activeEvent: null,
      hiddenNames: {},
      poolDrawerOpen: true,
      finalsLeft: [],
      finalsTrackHeight: '48px',
      cycleShot: ''
    }
  },
  computed: {
    innerWidth() {
      const count = (this.dateSlot > 0 ? this.returnLabels.length : this.labels.length) || this.lastDay
      if (this.dateSlot > 0) {
        const slots = Math.max(count - 1, 0) * this.dateSlot
        return `${52 + slots + 78 + 18}px`
      }
      if (count > 18) return `${Math.max(count * 48, 720)}px`
      return '100%'
    },
    labels() {
      if (this.axisLabels && this.axisLabels.length) return this.axisLabels
      return dayLabels(this.lastDay)
    },
    returnLabels() {
      if ((this.pathMode === 'wave' || this.pathMode === 'peak') && this.waveAxisLabels && this.waveAxisLabels.length) return this.waveAxisLabels
      return this.labels
    },
    lastIndex() {
      return Math.max(0, this.labels.length - 1)
    },
    heightLegendItems() {
      return (this.boardStocks || []).map((stock) => ({
        code: stock.code,
        name: stock.name,
        color: stock.color
      }))
    },
    legendItems() {
      if (this.pathMode === 'peak') {
        return this.stocks.map((stock) => ({ name: stock.name, color: stock.color, last: 0 }))
      }
      const daily = this.pathMode === 'wave'
      const labels = daily ? this.returnLabels : this.labels
      const ranked = this.stocks.map((stock) => {
        const source = this.pathMode === 'peak' && Array.isArray(stock.peakPoints)
          ? stock.peakPoints
          : this.pathMode === 'wave' && Array.isArray(stock.wavePoints)
            ? stock.wavePoints
            : stock.points
        const rows = (source || []).slice(0, labels.length)
        let last = null
        for (let i = rows.length - 1; i >= 0; i -= 1) {
          const n = Number(rows[i] && rows[i].cumulativeReturn)
          if (Number.isFinite(n)) {
            last = n
            break
          }
        }
        return { name: stock.name, color: stock.color, last: last == null ? -Infinity : last }
      })
      ranked.sort((a, b) => b.last - a.last || String(a.name).localeCompare(String(b.name), 'zh'))
      return ranked
    },
    burstStatDate() {
      const today = this.burstToday()
      return today.length >= 10 ? today.slice(5) : today
    },
    burstStatRows() {
      const labels = this.returnLabels || []
      const today = this.burstToday()
      const rows = []
      ;(this.stocks || []).forEach((stock) => {
        const marks = (this.volumeMarks && this.volumeMarks[stock.code]) || stock.volumeMarks || []
        if (!marks.length) return
        const source = this.pathMode === 'peak' && Array.isArray(stock.peakPoints)
          ? stock.peakPoints
          : this.pathMode === 'wave' && Array.isArray(stock.wavePoints)
            ? stock.wavePoints
            : stock.points
        const points = source || []
        marks.forEach((mark) => {
          if (!today || mark.date !== today) return
          const index = labels.indexOf(mark.date)
          if (index < 0) return
          const boardsN = Math.floor(Number(mark.boards))
          const high = mark.kind === 'high'
          if (!high && !(boardsN >= 2 && boardsN <= 4)) return
          const ret = Number(applyScenario(points[index] && points[index].cumulativeReturn, this.sourceId))
          rows.push({
            code: stock.code,
            name: stock.name,
            color: high ? '#e53935' : '#e6a23c',
            eventName: high ? '高位爆量' : '爆量',
            date: mark.date,
            shortDate: String(mark.date || '').slice(5),
            boards: boardsN,
            ret: Number.isFinite(ret) ? ret : null,
            dayIndex: index,
            description: high
              ? '4板及以上的高位爆量。监控池内为达到4板之后，当日成交量大于前5日均量，并且大于昨日成交量。'
              : '2板至4板，按 V2A 判定为爆量。'
          })
        })
      })
      rows.sort((a, b) => {
        if (a.ret == null && b.ret == null) return String(a.date).localeCompare(String(b.date)) || String(a.name).localeCompare(String(b.name), 'zh')
        if (a.ret == null) return 1
        if (b.ret == null) return -1
        return b.ret - a.ret || String(b.date).localeCompare(String(a.date)) || String(a.name).localeCompare(String(b.name), 'zh')
      })
      return rows
    }
  },
  watch: {
    stocks: { deep: true, handler() { this.scheduleRender('return') } },
    lastDay() { this.scheduleRender('both') },
    sourceId() { this.scheduleRender('return') },
    showEvents() { this.scheduleRender('return') },
    showBoardLabels() { this.scheduleRender('return') },
    activeCode() { this.scheduleRender('both') },
    activeDay() { this.scheduleRender('both') },
    axisLabels() { this.scheduleRender('both') },
    pathMode() { this.scheduleRender('return') },
    waveAxisLabels() { this.scheduleRender('return') },
    dayMarks: { deep: true, handler() { this.scheduleRender('return') } },
    boardStocks: { deep: true, handler() { this.scheduleRender('height') } },
    volumeMarks: { deep: true, handler() { this.scheduleRender('both') } },
    hitCodes: { deep: true, handler() { this.scheduleRender('both') } },
    finalsByDate: { deep: true, handler() { this.$nextTick(() => this.syncFinals()) } },
    innerWidth() {
      this.$nextTick(() => this.resize())
    }
  },
  mounted() {
    this.returnChart = echarts.init(this.$refs.returnEl)
    this.heightChart = echarts.init(this.$refs.heightEl)
    this.returnChart.on('finished', () => {
      if (!this.returnChart) return
      this.cycleShot = this.returnChart.getDataURL({ type: 'png', pixelRatio: 1, backgroundColor: '#fff' })
    })
    this.returnChart.on('click', (params) => this.onChartClick(params))
    this.heightChart.on('click', (params) => this.onHeightClick(params))
    this.bindBlankLinePick(this.returnChart, () => this.returnLabels)
    this.bindBlankLinePick(this.heightChart, () => this.labels)
    window.addEventListener('resize', this.resize)
    this.scheduleRender('both')
  },
  beforeDestroy() {
    if (this._renderRaf) cancelAnimationFrame(this._renderRaf)
    window.removeEventListener('resize', this.resize)
    if (this.returnChart) this.returnChart.dispose()
    if (this.heightChart) this.heightChart.dispose()
  },
  methods: {
    onPoolLeaderSelect(leader) {
      const code = String((leader && leader.code) || '').split('.')[0]
      if (code) this.$emit('toggle-pool', code)
    },
    resize() {
      if (this.returnChart) this.returnChart.resize()
      if (this.heightChart) this.heightChart.resize()
      if (this.returnChart) {
        this.cycleShot = this.returnChart.getDataURL({ type: 'png', pixelRatio: 1, backgroundColor: '#fff' })
      }
      this.$nextTick(() => this.syncFinals())
    },
    scheduleRender(part) {
      if (part !== 'height') this._needReturn = true
      if (part !== 'return') this._needHeight = true
      if (this._renderRaf) return
      this._renderRaf = requestAnimationFrame(() => {
        this._renderRaf = 0
        const drawReturn = this._needReturn
        const drawHeight = this._needHeight
        this._needReturn = false
        this._needHeight = false
        this.render(drawReturn, drawHeight)
      })
    },
    syncFinals() {
      if (!this.dateSlot || !this.returnChart || !this.$refs.returnEl || !this.$refs.finalsTrack) return
      const canvas = this.$refs.returnEl.getBoundingClientRect()
      const track = this.$refs.finalsTrack.getBoundingClientRect()
      const lefts = this.returnLabels.map((date) => {
        const x = this.returnChart.convertToPixel({ xAxisIndex: 0 }, date)
        if (!Number.isFinite(x)) return 0
        return canvas.left + x - track.left - this.dateSlot / 2
      })
      this.finalsLeft = lefts
      this.$nextTick(() => {
        const cols = this.$refs.finalsCols || []
        const height = cols.reduce((max, el) => Math.max(max, el ? el.offsetHeight : 0), 48)
        this.finalsTrackHeight = `${height}px`
      })
    },
    exportReturnPng() {
      if (!this.returnChart) return
      const url = this.returnChart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
      const link = document.createElement('a')
      link.href = url
      link.download = 'cycle-lifecycle-demo.png'
      link.click()
    },
    volumeDots(stock, boards, labels) {
      const marks = (this.volumeMarks && this.volumeMarks[stock.code]) || stock.volumeMarks || []
      const items = []
      let carried = null
      const yAt = (boards || []).map((board) => {
        const value = Number(board)
        if (Number.isFinite(value)) carried = value
        return Number.isFinite(value) ? value : carried
      })
      marks.forEach((mark) => {
        const index = labels.indexOf(mark.date)
        const value = yAt[index]
        if (index < 0 || !Number.isFinite(value)) return
        const boardsN = Math.floor(Number(mark.boards))
        const high = mark.kind === 'high'
        if (!high && boardsN >= 2 && boardsN <= 4) {
          items.push(this.volumeEventItem(stock, labels[index], value, index, mark, '#e6a23c', '爆量', '2板至4板，按 V2A 判定为爆量。'))
        }
        if (high) {
          items.push(this.volumeEventItem(stock, labels[index], value, index, mark, '#e53935', '高位爆量', '4板及以上的高位爆量。监控池内为达到4板之后，当日成交量大于前5日均量，并且大于昨日成交量。'))
        }
      })
      return this.clusterVolumeEvents(items)
    },
    volumeEventItem(stock, date, value, index, mark, color, name, description) {
      const boardsN = Math.floor(Number(mark.boards))
      return {
        date,
        value,
        event: {
          stockName: stock.name,
          stockCode: stock.code,
          color,
          eventName: name,
          tradeDate: mark.date,
          boardHeight: boardsN || Math.round(value),
          eventDescription: description,
          dayIndex: index
        }
      }
    },
    clusterVolumeEvents(items) {
      const groups = new Map()
      items.forEach((item) => {
        const key = `${item.date}|${item.value}`
        if (!groups.has(key)) groups.set(key, [])
        groups.get(key).push(item)
      })
      const marks = []
      groups.forEach((list) => {
        const first = list[0]
        const many = list.length > 1
        const lead = list.find((item) => item.event.eventName === '高位爆量') || first
        const color = lead.event.color
        marks.push({
          coord: [first.date, first.value],
          value: [first.date, first.value],
          symbol: 'circle',
          symbolSize: many ? 24 : 22,
          itemStyle: { color, borderColor: '#fff', borderWidth: 2 },
          event: many ? null : first.event,
          events: many ? list.map((item) => item.event) : null,
          stopText: many ? `${list.length} 个事件` : first.event.eventName,
          label: many
            ? {
              show: true,
              formatter: String(list.length),
              color: color === '#e53935' ? '#fff' : '#3d2914',
              fontSize: 12,
              fontWeight: 800,
              position: 'inside'
            }
            : { show: false }
        })
      })
      return marks
    },
    looseHighPins(labels, heightSource) {
      const covered = {}
      ;(heightSource || []).forEach((stock) => {
        const points = stock.points || []
        let seen = false
        points.forEach((point) => {
          if (point && point.dailyBoardHeight != null) seen = true
        })
        if (seen) covered[stock.code] = true
      })
      const names = {}
      ;(this.stocks || []).forEach((stock) => { names[stock.code] = stock.name })
      ;(heightSource || []).forEach((stock) => { names[stock.code] = stock.name })
      const items = []
      Object.keys(this.volumeMarks || {}).forEach((code) => {
        if (covered[code]) return
        ;(this.volumeMarks[code] || []).forEach((mark) => {
          if (mark.kind !== 'high') return
          const index = labels.indexOf(mark.date)
          const boards = Math.floor(Number(mark.boards))
          if (index < 0 || !boards) return
          items.push(this.volumeEventItem(
            { code, name: names[code] || code },
            labels[index],
            boards,
            index,
            mark,
            '#e53935',
            '高位爆量',
            '4板及以上的高位爆量。监控池内为达到4板之后，当日成交量大于前5日均量，并且大于昨日成交量。'
          ))
        })
      })
      return this.clusterVolumeEvents(items)
    },
    burstToday() {
      return this.tradeDate || (this.returnLabels || []).slice(-1)[0] || ''
    },
    openBurstStat(row) {
      this.activeEvent = {
        stockName: row.name,
        stockCode: row.code,
        color: row.color,
        eventName: row.eventName,
        tradeDate: row.date,
        boardHeight: row.boards,
        eventDescription: row.description,
        dayIndex: row.dayIndex
      }
      if (row.code) this.$emit('select-stock', row.code)
      if (Number.isFinite(row.dayIndex)) this.$emit('select-day', row.dayIndex)
    },
    openVolumeEvent(item) {
      this.activeEvent = { ...item, backTo: this.activeEvent }
      if (item.stockCode) this.$emit('select-stock', item.stockCode)
      if (Number.isFinite(item.dayIndex)) this.$emit('select-day', item.dayIndex)
    },
    openChartMark(params) {
      const data = params && params.data
      if (!data) return false
      const isMark = params.componentType === 'markPoint' || params.seriesId === '__high_pins'
      if (!isMark) return false
      if (data.events && data.events.length) {
        const first = data.events[0]
        const sameStock = data.events.every((item) => item.stockCode === first.stockCode)
        this.activeEvent = {
          stockName: sameStock ? first.stockName : '重叠事件',
          tradeDate: first.tradeDate,
          events: data.events
        }
        if (sameStock && first.stockCode) this.$emit('select-stock', first.stockCode)
        return true
      }
      if (!data.event) return false
      this.activeEvent = data.event
      if (data.event.stockCode) this.$emit('select-stock', data.event.stockCode)
      if (Number.isFinite(data.event.dayIndex)) this.$emit('select-day', data.event.dayIndex)
      return true
    },
    heightLaneOffsets(stocks) {
      const paths = (stocks || []).map((stock) => (stock.points || []).map((point) => (
        point && point.dailyBoardHeight != null ? point.dailyBoardHeight : null
      )))
      const parent = paths.map((_, index) => index)
      const find = (index) => {
        let cursor = index
        while (parent[cursor] !== cursor) {
          parent[cursor] = parent[parent[cursor]]
          cursor = parent[cursor]
        }
        return cursor
      }
      const unite = (left, right) => {
        const a = find(left)
        const b = find(right)
        if (a !== b) parent[b] = a
      }
      const sharesBoard = (left, right) => {
        const length = Math.max(left.length, right.length)
        for (let i = 0; i < length; i += 1) {
          if (left[i] != null && right[i] != null && left[i] === right[i]) return true
        }
        return false
      }
      for (let i = 0; i < paths.length; i += 1) {
        for (let j = i + 1; j < paths.length; j += 1) {
          if (sharesBoard(paths[i], paths[j])) unite(i, j)
        }
      }
      const groups = {}
      for (let i = 0; i < paths.length; i += 1) {
        const root = find(i)
        if (!groups[root]) groups[root] = []
        groups[root].push(i)
      }
      const offsets = paths.map(() => 0)
      const step = 0.18
      Object.keys(groups).forEach((key) => {
        const members = groups[key]
        if (members.length < 2) return
        members.forEach((index, lane) => {
          offsets[index] = (lane - (members.length - 1) / 2) * step
        })
      })
      return offsets
    },
    premiumDelta(pct) {
      const capped = Math.max(-20, Math.min(20, Number(pct) || 0))
      return Math.round(capped / 20 * 1.4 * 10) / 10
    },
    premiumExtensions(stock, labels, offset) {
      const boards = (stock.points || []).map((point) => point && point.dailyBoardHeight != null ? point.dailyBoardHeight : null)
      const lines = []
      const shift = Number(offset) || 0
      boards.forEach((value, index) => {
        if (value == null) return
        if (boards[index + 1] != null) return
        const note = stock.openNotes && stock.openNotes[index]
        const nextLabel = labels[index + 1]
        if (!note || !Number.isFinite(note.pct) || !nextLabel) return
        const up = note.pct >= 0
        const arrowColor = up ? '#f25c54' : '#16a34a'
        const endY = Math.max(0, Math.round((value + shift + this.premiumDelta(note.pct)) * 10) / 10)
        lines.push([
          {
            coord: [labels[index], value + shift],
            symbol: 'none',
            lineStyle: { color: stock.color, width: 1.6, type: 'solid' }
          },
          {
            coord: [nextLabel, endY],
            symbol: 'arrow',
            symbolSize: 8,
            lineStyle: { color: stock.color, width: 1.6 },
            itemStyle: { color: arrowColor, borderColor: arrowColor },
            label: {
              show: true,
              formatter: `${note.pct > 0 ? '+' : ''}${note.pct.toFixed(1)}%`,
              color: arrowColor,
              fontSize: 11,
              fontWeight: 700,
              position: up ? 'top' : 'bottom'
            }
          }
        ])
      })
      return lines
    },
    heightAxisTip(params, heightSource) {
      const list = (Array.isArray(params) ? params : [params]).filter((item) => item && item.seriesId !== '__high_pins' && item.value != null && item.value !== '' && !Array.isArray(item.value))
      if (!list.length) return ''
      const maxBoard = Math.max(...list.map((item) => Number(item.value)))
      const date = list[0].axisValue || list[0].name
      const tops = list.filter((item) => Number(item.value) === maxBoard).sort((a, b) => {
        const left = heightSource.find((stock) => stock.code === a.seriesId)
        const right = heightSource.find((stock) => stock.code === b.seriesId)
        const leftTime = this.limitUpTimeKey(left && left.timeByDate && left.timeByDate[date])
        const rightTime = this.limitUpTimeKey(right && right.timeByDate && right.timeByDate[date])
        return leftTime.localeCompare(rightTime) || String(a.seriesName).localeCompare(String(b.seriesName), 'zh')
      })
      return tops.map((item) => {
        const stock = heightSource.find((row) => row.code === item.seriesId)
        const note = stock && stock.openNotes && stock.openNotes[item.dataIndex]
        const time = stock && stock.timeByDate && stock.timeByDate[date]
        const openText = note ? `<br/>${note.text}` : ''
        const burstMark = (stock && (this.volumeMarks[stock.code] || stock.volumeMarks) || []).find((mark) => mark.date === date)
        const burstText = burstMark ? `<br/>${burstMark.kind === 'high' ? '高位爆量' : '爆量'}` : ''
        const board = Math.round(Number(item.value))
        return `${item.seriesName}${time ? ' ' + time : ''}<br/>${date} · ${board}B${openText}${burstText}`
      }).join('<br/><br/>')
    },
    limitUpTimeKey(time) {
      const digits = String(time || '').replace(/\D/g, '')
      return digits ? digits.padStart(6, '0') : '999999'
    },
    onHeightClick(params) {
      this._linePicked = true
      if (this.openChartMark(params)) return
      if (!params || !params.seriesId || params.seriesId === '__guide_h') return
      this.$emit('select-stock', params.seriesId)
      const stock = (this.boardStocks || []).find((item) => item.code === params.seriesId)
      const note = stock && stock.openNotes && stock.openNotes[params.dataIndex]
      if (!stock || !note) return
      this.activeEvent = {
        stockName: stock.name,
        stockCode: stock.code,
        color: note.plus10 ? '#f25c54' : '#409eff',
        eventName: note.plus10 ? '次日+10%开' : '次日开盘',
        tradeDate: this.shortDate(this.labels[params.dataIndex]),
        boardHeight: note.boards,
        nextDayRepairStatus: note.text,
        eventDescription: note.plus10 ? '次日开盘涨幅达到 10% 及以上。' : '次日开盘涨幅没有到 10%。',
        dayIndex: params.dataIndex
      }
      this.$emit('select-stock', stock.code)
    },
    onChartClick(params) {
      this._linePicked = true
      if (this.openChartMark(params)) return
      if (params.componentType === 'series' && params.seriesId) {
        this.$emit('select-stock', String(params.seriesId).replace(/:tail$/, ''))
        if (Number.isFinite(params.dataIndex)) this.$emit('select-day', params.dataIndex)
      }
    },
    finalsOn(date) {
      const list = this.finalsByDate && this.finalsByDate[date]
      return Array.isArray(list) ? list : []
    },
    isConceptHit(stock) {
      const code = String((stock && stock.code) || '').split('.')[0]
      return !!(code && this.hitCodes && this.hitCodes[code])
    },
    inkOn(hex) {
      const raw = String(hex || '').replace('#', '')
      if (!/^[0-9a-fA-F]{6}$/.test(raw)) return '#fff'
      const channel = (index) => {
        const value = parseInt(raw.slice(index, index + 2), 16) / 255
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4)
      }
      const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4)
      return luminance > 0.36 ? '#000' : '#fff'
    },
    hitStyle(stock) {
      if (!this.isConceptHit(stock) || !this.hitColor) return null
      const ink = this.inkOn(this.hitColor)
      return {
        background: this.hitColor,
        color: ink,
        fontWeight: 800
      }
    },
    bindBlankLinePick(chart, labelsOf) {
      chart.getZr().on('click', (event) => {
        const point = [event.offsetX, event.offsetY]
        setTimeout(() => {
          if (this._linePicked) {
            this._linePicked = false
            return
          }
          const code = this.nearestLineCode(chart, point, labelsOf())
          if (code) this.$emit('select-stock', code)
        }, 0)
      })
    },
    nearestLineCode(chart, point, labels) {
      const option = chart.getOption() || {}
      const series = option.series || []
      let bestCode = ''
      let bestDist = 18
      for (let s = 0; s < series.length; s += 1) {
        const row = series[s]
        if (!row || !row.id || String(row.id).indexOf('__') === 0) continue
        const data = row.data || []
        let prev = null
        for (let i = 0; i < data.length; i += 1) {
          const value = this.readPoint(data[i])
          const date = labels[i]
          if (!date || !Number.isFinite(value)) {
            prev = null
            continue
          }
          let pixel = null
          try {
            pixel = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [date, value])
          } catch (err) {
            pixel = null
          }
          if (!pixel || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) {
            prev = null
            continue
          }
          if (prev) {
            const dist = this.distanceToSegment(point, prev, pixel)
            if (dist < bestDist) {
              bestDist = dist
              bestCode = row.id
            }
          }
          const pointDist = Math.hypot(point[0] - pixel[0], point[1] - pixel[1])
          if (pointDist < bestDist) {
            bestDist = pointDist
            bestCode = row.id
          }
          prev = pixel
        }
      }
      return String(bestCode || '').replace(/:tail$/, '')
    },
    distanceToSegment(point, start, end) {
      const dx = end[0] - start[0]
      const dy = end[1] - start[1]
      const len2 = dx * dx + dy * dy
      if (!len2) return Math.hypot(point[0] - start[0], point[1] - start[1])
      const t = Math.max(0, Math.min(1, ((point[0] - start[0]) * dx + (point[1] - start[1]) * dy) / len2))
      return Math.hypot(point[0] - (start[0] + t * dx), point[1] - (start[1] + t * dy))
    },
    peakTailSeries(labels) {
      if (this.pathMode !== 'peak') return []
      const stock = (this.stocks || []).find((item) => item.code === this.activeCode)
      const tail = stock && stock.peakTail
      if (!stock || !Array.isArray(tail) || !tail.length) return []
      const tailMap = {}
      tail.forEach((point) => { tailMap[point.date] = Number(point.cum) })
      const points = stock.peakPoints || []
      let lastReal = -1
      const data = labels.map((date, index) => {
        const raw = Number(applyScenario(points[index] && points[index].cumulativeReturn, this.sourceId))
        if (Number.isFinite(raw)) lastReal = index
        const extra = tailMap[date]
        if (!Number.isFinite(extra)) return null
        const shown = Number(applyScenario(extra, this.sourceId))
        return Number.isFinite(shown) ? shown : null
      })
      if (lastReal >= 0) {
        const shown = Number(applyScenario(points[lastReal] && points[lastReal].cumulativeReturn, this.sourceId))
        if (Number.isFinite(shown)) data[lastReal] = shown
      }
      const hasTail = data.some((value, index) => index > lastReal && value != null)
      if (!hasTail) return []
      return [{
        id: `${stock.code}:tail`,
        name: stock.name,
        type: 'line',
        data,
        showSymbol: false,
        connectNulls: false,
        triggerLineEvent: true,
        z: 11,
        lineStyle: { width: 4, type: 'dashed', color: stock.color, opacity: this.lineOpacity(stock, this.liftOpacityByCode()) },
        itemStyle: { color: stock.color }
      }]
    },
    lineLifted(stock) {
      const code = String((stock && stock.code) || '').split('.')[0]
      if (!code) return false
      if (code === String(this.activeCode || '').split('.')[0]) return true
      return !!(this.hitCodes && this.hitCodes[code])
    },
    endingReturn(stock) {
      const sourcePoints = this.pathMode === 'peak' && Array.isArray(stock.peakPoints)
        ? stock.peakPoints
        : this.pathMode === 'wave' && Array.isArray(stock.wavePoints)
          ? stock.wavePoints
          : stock.points
      const limit = this.pathMode === 'straight' ? (sourcePoints || []).length : this.returnLabels.length
      let last = null
      ;(sourcePoints || []).slice(0, limit).forEach((point) => {
        const n = Number(applyScenario(point && point.cumulativeReturn, this.sourceId))
        if (Number.isFinite(n)) last = n
      })
      return last
    },
    liftOpacityByCode() {
      const lifted = (this.stocks || []).filter((stock) => this.lineLifted(stock))
      if (!lifted.length) return null
      lifted.sort((a, b) => {
        const av = this.endingReturn(a)
        const bv = this.endingReturn(b)
        if (av == null && bv == null) return String(a.name).localeCompare(String(b.name), 'zh')
        if (av == null) return 1
        if (bv == null) return -1
        return bv - av || String(a.name).localeCompare(String(b.name), 'zh')
      })
      const map = {}
      lifted.forEach((stock, index) => {
        map[String(stock.code || '').split('.')[0]] = this.rankOpacity(index + 1)
      })
      return map
    },
    rankOpacity(rank) {
      if (rank <= 1) return 1
      const value = 1 - (rank - 1) * 0.05
      return Math.max(0.05, Math.round(value * 100) / 100)
    },
    lineOpacity(stock, opacityByCode) {
      if (!opacityByCode) return 1
      const code = String((stock && stock.code) || '').split('.')[0]
      if (opacityByCode[code] != null) return opacityByCode[code]
      if (this.lineLifted(stock)) return 1
      return 0.2
    },
    liftedStyle(active, color, opacity) {
      const shown = opacity == null ? (active ? 1 : 0.2) : opacity
      const shadow = active && shown >= 0.99 ? 'rgba(15, 23, 42, 0.62)' : 'transparent'
      const lineStyle = {
        width: active ? 8 : 2.2,
        color,
        opacity: shown,
        shadowBlur: active && shown >= 0.99 ? 22 : 0,
        shadowOffsetY: active && shown >= 0.99 ? 12 : 0,
        shadowColor: shadow
      }
      return {
        z: active ? 20 : 1,
        clip: false,
        symbolSize: active ? 14 : 6,
        triggerLineEvent: true,
        emphasis: { disabled: true },
        itemStyle: {
          color,
          opacity: shown,
          borderColor: '#fff',
          borderWidth: active ? 3 : 0,
          shadowBlur: active && shown >= 0.99 ? 16 : 0,
          shadowOffsetY: active && shown >= 0.99 ? 6 : 0,
          shadowColor: shadow
        },
        lineStyle
      }
    },
    expandText(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`
    },
    expandClass(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return ''
      return n >= 0 ? 'is-up' : 'is-down'
    },
    shortDate(value) {
      const text = String(value || '')
      return text.length >= 10 ? text.slice(5) : text
    },
    dayMarkLine() {
      if (this.activeDay < 0 || this.activeDay > this.lastIndex) return { data: [] }
      const xAxis = this.labels[this.activeDay]
      if (!xAxis) return { data: [] }
      return {
        symbol: 'none',
        silent: true,
        lineStyle: { color: '#409eff', type: 'dashed', width: 1 },
        label: { show: false },
        data: [{ xAxis }]
      }
    },
    eventPoints(stock, yPicker) {
      if (!this.showEvents) return []
      const rank = {
        volumeBreak: 1,
        ageSignal: 2,
        stallHigh: 3,
        accelerate: 4,
        nextLimitDown: 5,
        nextDeep: 6,
        nextOpenLow: 7,
        nextRepair: 8
      }
      const picked = new Map()
      EVENTS.filter((item) => item.stockCode === stock.code && item.dayIndex <= this.lastIndex && item.eventType !== 'volumeHigh' && this.labels[item.dayIndex]).forEach((item) => {
        const prev = picked.get(item.dayIndex)
        if (!prev || (rank[item.eventType] || 9) < (rank[prev.eventType] || 9)) picked.set(item.dayIndex, item)
      })
      const labeled = stock.code === this.activeCode
      return [...picked.values()].map((item) => {
        const meta = eventMeta(item.eventType)
        const point = stock.points[item.dayIndex]
        if (!point || this.pathMode === 'peak') return null
        return {
          name: meta.name,
          coord: [this.labels[item.dayIndex], yPicker(point, item)],
          symbolOffset: [0, -6],
          event: {
            ...item,
            stockName: stock.name,
            eventName: meta.name,
            color: meta.color,
            tradeDate: this.labels[item.dayIndex]
          },
          itemStyle: { color: meta.color },
          label: {
            show: labeled,
            formatter: meta.name,
            color: '#000',
            fontWeight: 600,
            fontSize: 12,
            position: 'top',
            distance: 6
          }
        }
      }).filter(Boolean)
    },
    legendNames() {
      return this.legendItems.map((item) => item.name)
    },
    burstEdgeMarks(stock, data, labels) {
      const marks = Array.isArray(stock.burstMarks) ? stock.burstMarks : []
      return marks.map((mark) => {
        const date = labels[mark.dayIndex]
        const value = this.readPoint(data[mark.dayIndex])
        if (!date || !Number.isFinite(value)) return null
        return {
          coord: [date, value],
          stopText: `${mark.label} · 当日 ${value.toFixed(1)}%`,
          symbol: 'circle',
          symbolSize: 9,
          itemStyle: { color: stock.color },
          label: { show: false }
        }
      }).filter(Boolean)
    },
    peakStopMarks(data, labels, color) {
      let best = null
      let index = -1
      data.forEach((value, i) => {
        const n = Number(value)
        if (!Number.isFinite(n)) return
        if (best == null || n > best) {
          best = n
          index = i
        }
      })
      if (index < 0) return []
      const date = labels[index]
      const text = String(date || '').length >= 10 ? String(date).slice(5) : String(date || '')
      return [{
        coord: [date, best],
        stopText: text,
        symbol: 'pin',
        symbolSize: 34,
        itemStyle: { color: color || '#000' },
        label: {
          show: true,
          formatter: `停${text}`,
          color: '#000',
          fontWeight: 700,
          fontSize: 11,
          position: 'top'
        }
      }]
    },
    toggleLegend(name) {
      this.$set(this.hiddenNames, name, !this.hiddenNames[name])
      const action = { type: 'legendToggleSelect', name }
      if (this.returnChart) this.returnChart.dispatchAction(action)
      if (this.heightChart) this.heightChart.dispatchAction(action)
    },
    dateAxisLabel(value, withMarks) {
      const dateText = String(value).length >= 10 ? String(value).slice(5) : String(value)
      if (!withMarks) return dateText
      const mark = (this.dayMarks && this.dayMarks[value]) || {}
      const score = Number(mark.nodeScore)
      const hasScore = Number.isFinite(score)
      const nodeText = hasScore ? `${Math.round(score)}${mark.nodeHit ? '★' : ''}` : '—'
      const nodeStyle = mark.nodeHit ? 'nodeHit' : 'node'
      const soil = mark.soil === 'S_PLUS' ? 'S+' : mark.soil === 'S' ? 'S' : '无'
      const soilStyle = mark.soil === 'S_PLUS' ? 'soilPlus' : mark.soil === 'S' ? 'soil' : 'soilNone'
      const burstCount = this.highBurstCount(value)
      const burstText = burstCount > 0 ? '●'.repeat(burstCount) : ' '
      return `{date|${dateText}}\n{${nodeStyle}|${nodeText}}\n{${soilStyle}|${soil}}\n{burst|${burstText}}`
    },
    highBurstCount(date) {
      const day = String(date || '').slice(0, 10)
      if (!day) return 0
      let count = 0
      const marks = this.volumeMarks || {}
      Object.keys(marks).forEach((code) => {
        const list = marks[code] || []
        list.forEach((mark) => {
          if (mark && mark.kind === 'high' && String(mark.date || '').slice(0, 10) === day) count += 1
        })
      })
      return count
    },
    readPoint(value) {
      if (value && typeof value === 'object') {
        if (Array.isArray(value)) return Number(value[1])
        return Number(value.value)
      }
      return Number(value)
    },
    percentExtent(seriesList) {
      let dataMin = 0
      let dataMax = 0
      for (const series of seriesList || []) {
        for (const value of series.data || []) {
          const n = this.readPoint(value)
          if (!Number.isFinite(n)) continue
          if (n < dataMin) dataMin = n
          if (n > dataMax) dataMax = n
        }
      }
      const axisMin = dataMin < -15 ? Math.floor(dataMin / 15) * 15 : -15
      const rawMax = dataMax > 0 ? Math.ceil((dataMax - 1e-9) / 15) * 15 : 15
      return {
        min: axisMin,
        max: rawMax <= axisMin ? axisMin + 15 : rawMax
      }
    },
    percentAxis(seriesList) {
      const extent = this.percentExtent(seriesList)
      return {
        type: 'value',
        min: extent.min,
        max: extent.max,
        interval: 15,
        axisLabel: { color: '#000', fontSize: 12, fontWeight: 600, formatter: '{value}%' },
        splitLine: { show: false }
      }
    },
    peakScale(seriesList, count) {
      const extent = this.percentExtent(seriesList)
      const el = this.$refs.returnEl
      const chartH = (el && el.clientHeight) || (this.dateSlot > 0 ? 614 : 440)
      const chartW = (el && el.clientWidth) || 0
      const plotH = Math.max(chartH - 16 - 68, 160)
      const plotW = chartW > 130 ? chartW - 52 - 78 : Math.max(count, 2) * 76
      return {
        min: extent.min,
        max: extent.max,
        plotH,
        stepX: count > 1 ? plotW / (count - 1) : 76
      }
    },
    layoutPeakLabels(seriesList, scale) {
      const span = Math.max(scale.max - scale.min, 1)
      const placed = []
      const shifts = [0]
      for (let step = 1; step <= 10; step += 1) {
        shifts.push(step * 24, -step * 24)
      }
      const items = []
      for (let seriesIndex = 0; seriesIndex < (seriesList || []).length; seriesIndex += 1) {
        const seriesData = seriesList[seriesIndex].data || []
        for (let dataIndex = 0; dataIndex < seriesData.length; dataIndex += 1) {
          const point = seriesData[dataIndex]
          if (!point || !point.labelText) continue
          items.push({
            point,
            dayIndex: dataIndex,
            value: point.value,
            text: point.labelText
          })
        }
      }
      items.sort((a, b) => a.dayIndex - b.dayIndex || b.value - a.value)
      items.forEach((item) => {
        let width = 14
        const text = String(item.text || '')
        for (let i = 0; i < text.length; i += 1) width += text.charCodeAt(i) > 255 ? 12 : 7
        const height = 22
        const cx = item.dayIndex * scale.stepX
        const py = (scale.max - item.value) / span * scale.plotH
        let best = null
        let bestScore = Infinity
        for (let s = 0; s < shifts.length; s += 1) {
          const dy = shifts[s]
          const bottom = py - 12 + dy
          const rect = { left: cx - width / 2, right: cx + width / 2, top: bottom - height, bottom }
          const outside = rect.top < 2 || rect.bottom > scale.plotH - 2
          let overlap = 0
          for (let p = 0; p < placed.length; p += 1) {
            const other = placed[p]
            const ox = Math.min(rect.right, other.right) - Math.max(rect.left, other.left)
            const oy = Math.min(rect.bottom, other.bottom) - Math.max(rect.top, other.top)
            if (ox > -4 && oy > -4) overlap += Math.max(ox, 0) * Math.max(oy, 0) + 1
          }
          if (!overlap && !outside) {
            best = { dy, rect }
            break
          }
          const score = overlap + (outside ? 8000 : 0) + Math.abs(dy) * 0.01
          if (score < bestScore) {
            bestScore = score
            best = { dy, rect }
          }
        }
        if (!best) return
        placed.push(best.rect)
        item.point.label.offset = [0, best.dy]
      })
    },
    dateAxisRich() {
      return {
        date: { color: '#000', fontSize: 12, fontWeight: 600, lineHeight: 16, align: 'center' },
        node: { color: '#000', fontSize: 12, fontWeight: 700, lineHeight: 16, align: 'center' },
        nodeHit: { color: '#0f766e', fontSize: 12, fontWeight: 700, lineHeight: 16, align: 'center' },
        soilPlus: { color: '#8B1515', fontSize: 12, fontWeight: 700, lineHeight: 16, align: 'center' },
        soil: { color: '#C2410C', fontSize: 12, fontWeight: 700, lineHeight: 16, align: 'center' },
        soilNone: { color: '#6b7280', fontSize: 12, fontWeight: 600, lineHeight: 16, align: 'center' },
        burst: { color: '#e53935', fontSize: 14, fontWeight: 700, lineHeight: 16, align: 'center' }
      }
    },
    render(drawReturn, drawHeight) {
      if (!this.returnChart || !this.heightChart) return
      const paintReturn = drawReturn !== false
      const paintHeight = drawHeight !== false
      const returnLabels = this.returnLabels
      const returnCount = returnLabels.length
      const labels = this.labels
      const count = labels.length
      const guide = {
        type: 'line',
        id: '__guide',
        name: '',
        data: [],
        silent: true,
        tooltip: { show: false },
        lineStyle: { opacity: 0 },
        itemStyle: { opacity: 0 },
        markArea: { silent: true, data: [] },
        markLine: this.dayMarkLine()
      }
      const opacityByCode = this.liftOpacityByCode()
      const returnSeries = this.stocks.map((stock) => {
        const lifted = this.lineLifted(stock)
        const shown = this.lineOpacity(stock, opacityByCode)
        const sourcePoints = this.pathMode === 'peak' && Array.isArray(stock.peakPoints)
          ? stock.peakPoints
          : this.pathMode === 'wave' && Array.isArray(stock.wavePoints)
            ? stock.wavePoints
            : stock.points
        const marks = {}
        ;(stock.burstMarks || []).forEach((mark) => { marks[mark.dayIndex] = mark })
        let waveLabelIndex = -1
        if (this.pathMode === 'wave') {
          sourcePoints.slice(0, returnCount).forEach((point, dayIndex) => {
            const n = Number(applyScenario(point && point.cumulativeReturn, this.sourceId))
            if (Number.isFinite(n)) waveLabelIndex = dayIndex
          })
        }
        const data = sourcePoints.slice(0, returnCount).map((point, dayIndex) => {
          const raw = applyScenario(point && point.cumulativeReturn, this.sourceId)
          const n = Number(raw)
          const mark = this.pathMode === 'peak' ? marks[dayIndex] : null
          const waveLabel = this.pathMode === 'wave' && dayIndex === waveLabelIndex && Number.isFinite(n)
          if ((!mark && !waveLabel) || !Number.isFinite(n)) return raw
          const text = mark
            ? mark.label
            : `${stock.name} ${n > 0 ? '+' : ''}${n.toFixed(1)}%`
          return {
            value: n,
            labelText: text,
            label: {
              position: 'top',
              distance: 8,
              offset: [0, 0],
              color: stock.color,
              fontWeight: 700,
              fontSize: 11,
              backgroundColor: 'rgba(255,255,255,0.94)',
              borderColor: stock.color,
              borderWidth: 1,
              borderRadius: 3,
              padding: [2, 5]
            }
          }
        })
        return {
          id: stock.code,
          name: stock.name,
          type: 'line',
          data,
          showSymbol: true,
          showAllSymbol: true,
          connectNulls: false,
          symbol: 'circle',
          ...this.liftedStyle(lifted, stock.color, shown),
          label: {
            show: this.pathMode === 'peak' || this.pathMode === 'wave',
            opacity: shown,
            formatter: (params) => (params.data && params.data.labelText) || ''
          },
          labelLayout: this.pathMode === 'peak' || this.pathMode === 'wave' ? { hideOverlap: false } : undefined,
          endLabel: {
            show: this.pathMode === 'straight',
            formatter: (params) => {
              if (params.value == null || params.value === '') return ''
            const value = Number(params.value)
              if (!Number.isFinite(value)) return ''
              return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
            },
            color: lifted ? stock.color : 'rgba(0,0,0,0.55)',
            opacity: shown,
            fontWeight: lifted ? 800 : 700,
            fontSize: lifted ? 15 : 12
          },
          markPoint: {
            symbol: 'circle',
            symbolSize: 22,
            clip: false,
            z: 30,
            data: (this.pathMode === 'peak'
              ? (stock.burstMarks ? this.burstEdgeMarks(stock, data, returnLabels) : this.peakStopMarks(data, returnLabels, stock.color))
              : this.pathMode === 'wave'
                ? []
                : this.eventPoints(stock, (point) => applyScenario(point.cumulativeReturn, this.sourceId))
            ).concat(this.volumeDots(stock, data.map((item) => this.readPoint(item)), returnLabels))
          }
        }
      })
      const tailSeries = this.peakTailSeries(returnLabels)
      const labelScale = this.pathMode === 'peak' || this.pathMode === 'wave'
        ? this.peakScale(returnSeries.concat(tailSeries), returnCount)
        : null
      if (labelScale) this.layoutPeakLabels(returnSeries, labelScale)
      const legend = this.legendNames()
      const legendOpt = { show: false, data: legend }
      const baseGrid = { left: 52, right: 78, top: 16, bottom: 28 }
      const returnGrid = { left: 52, right: 78, top: 16, bottom: 84 }
      const paint = { notMerge: true, lazyUpdate: true }
      if (paintReturn) this.returnChart.setOption({
        animation: false,
        backgroundColor: '#ffffff',
        textStyle: { color: '#000' },
        color: this.stocks.map((stock) => stock.color),
        legend: legendOpt,
        tooltip: {
          trigger: 'item',
          formatter: (params) => {
            if (params.componentType === 'markPoint' && params.data && params.data.stopText) {
              return `${params.seriesName}<br/>${params.data.stopText}`
            }
            if (params.componentType === 'markPoint' && params.data && params.data.event) {
              const event = params.data.event
              return `${event.stockName}<br/>${event.eventName}<br/>${event.tradeDate} · ${event.boardHeight}B<br/><span style="color:#000">演示数据</span>`
            }
            const rawValue = params.data && typeof params.data === 'object' && params.data.value != null ? params.data.value : params.value
            const value = Array.isArray(rawValue) ? rawValue[1] : rawValue
            if (value == null || value === '' || !Number.isFinite(Number(value))) return ''
            const note = String(params.seriesId || '').endsWith(':tail')
              ? '离榜后，沿用最后在榜那天的起点'
              : this.pathMode === 'peak' ? '在榜当日涨幅' : this.pathMode === 'wave' ? '每日收盘累计' : '演示数据'
            return `${params.seriesName}<br/>${params.name} · ${Number(value).toFixed(1)}%<br/><span style="color:#000">${note}</span>`
          }
        },
        grid: returnGrid,
        xAxis: {
          type: 'category',
          data: returnLabels,
          boundaryGap: false,
          axisLine: { lineStyle: { color: '#d5e3f2' } },
          axisLabel: {
            color: '#000',
            fontSize: 12,
            fontWeight: 600,
            interval: 0,
            lineHeight: 16,
            formatter: (value) => this.dateAxisLabel(value, true),
            rich: this.dateAxisRich()
          }
        },
        yAxis: this.percentAxis(returnSeries.concat(tailSeries)),
        series: [guide, ...returnSeries, ...tailSeries]
      }, paint)
      if (!paintHeight) return
      const heightSource = this.boardStocks || []
      const heightOffsets = this.heightLaneOffsets(heightSource)
      const heightSeries = heightSource.map((stock, index) => {
        const shift = heightOffsets[index] || 0
        const boards = (stock.points || []).slice(0, count).map((point) => {
          if (!point || point.dailyBoardHeight == null) return null
          return point.dailyBoardHeight + shift
        })
        return {
          id: stock.code,
          name: stock.name,
          type: 'line',
          data: boards,
          showSymbol: true,
          showAllSymbol: true,
          connectNulls: false,
          symbol: 'circle',
          ...this.liftedStyle(this.lineLifted(stock), stock.color, this.lineOpacity(stock, opacityByCode)),
          markLine: {
            silent: true,
            symbol: ['none', 'arrow'],
            lineStyle: { color: stock.color, width: 1.6 },
            data: this.premiumExtensions(stock, labels, shift)
          },
          markPoint: {
            symbol: 'circle',
            symbolSize: 22,
            clip: false,
            z: 30,
            data: this.volumeDots(stock, boards, labels)
          }
        }
      })
      let heightMax = 8
      heightSource.forEach((stock) => {
        const points = stock.points || []
        points.forEach((point) => {
          const n = Number(point && point.dailyBoardHeight)
          if (Number.isFinite(n) && n + 1.6 > heightMax) heightMax = n + 1.6
        })
      })
      this.heightChart.setOption({
        animation: false,
        backgroundColor: '#ffffff',
        textStyle: { color: '#000' },
        color: heightSource.map((stock) => stock.color),
        legend: { show: false },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => this.heightAxisTip(params, heightSource)
        },
        grid: baseGrid,
        xAxis: { type: 'category', data: labels, boundaryGap: false, axisLine: { lineStyle: { color: '#d5e3f2' } }, axisLabel: { color: '#000', fontSize: 12, fontWeight: 600, rotate: this.dateSlot > 0 ? 0 : (count > 16 ? 40 : 0), interval: 0, formatter: (value) => String(value).length >= 10 ? String(value).slice(5) : value } },
        yAxis: {
          type: 'value',
          min: 0,
          max: Math.ceil(heightMax),
          interval: 1,
          axisLabel: { color: '#000', fontSize: 12, fontWeight: 600, formatter: '{value}B' },
          splitLine: { show: false }
        },
        series: [{ ...guide, id: '__guide_h' }, ...heightSeries, {
          id: '__high_pins',
          name: '高位爆量',
          type: 'scatter',
          data: this.looseHighPins(labels, heightSource),
          symbol: 'circle',
          symbolSize: 22,
          itemStyle: { color: '#e53935', borderColor: '#fff', borderWidth: 2 },
          z: 31,
          label: {
            show: true,
            fontSize: 12,
            fontWeight: 700,
            formatter: (params) => {
              const label = params.data && params.data.label
              return label && label.formatter ? label.formatter : ''
            }
          }
        }]
      }, paint)
    }
  }
}
</script>

<style lang="scss" scoped>
.life-charts {
  position: relative;
}
.chart-scroll {
  overflow-x: auto;
}
.chart-row {
  display: flex;
  align-items: flex-start;
  width: max-content;
}
.day-expand--band {
  flex: none;
  height: 168px;
  margin: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #d7e6f5;
  border-radius: 8px;
  padding: 6px 8px 4px;
  box-sizing: border-box;
}
.day-expand--band ol {
  height: 132px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 0 14px;
}
.day-expand--band li { height: 22px; min-height: 22px; }
.day-expand--band .day-expand__name { flex: none; max-width: 84px; }
.day-expand__title {
  font-size: 14px;
  font-weight: 700;
  color: #1f4e79;
  padding: 0 4px 6px;
}
.day-expand ol {
  list-style: none;
  margin: 0;
  padding: 0;
}
.day-expand li {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 28px;
  padding: 2px 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  color: #303133;
}
.day-expand li.is-on { background: #f3f8fd; }
.day-expand li i {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex: none;
}
.day-expand__no {
  width: 18px;
  text-align: right;
  font-weight: 700;
  color: #000;
  flex: none;
}
.day-expand__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.day-expand b { flex: none; font-weight: 700; }
.day-expand b.is-up { color: #f25c54; }
.day-expand b.is-down { color: #16a34a; }
.day-expand__empty { color: #909399; font-size: 12px; padding: 4px; }
.chart-plot { position: relative; }
.burst-stat {
  position: absolute;
  left: 58px;
  top: 8px;
  z-index: 4;
  width: 246px;
  max-height: 188px;
  overflow: auto;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #d7e6f5;
  border-radius: 8px;
  padding: 6px 8px 4px;
}
.burst-stat__title {
  position: sticky;
  top: 0;
  z-index: 1;
  font-size: 13px;
  font-weight: 700;
  color: #1f4e79;
  padding-bottom: 4px;
  background: rgba(255, 255, 255, 0.96);
}
.burst-stat ol { list-style: none; margin: 0; padding: 0; }
.burst-stat li {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  padding: 1px 2px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #303133;
}
.burst-stat li.is-on { background: #f3f8fd; }
.burst-stat li i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}
.burst-stat__no {
  width: 16px;
  text-align: right;
  font-weight: 700;
  color: #000;
  flex: none;
}
.burst-stat__name {
  width: 58px;
  flex: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.burst-stat__date { color: #909399; flex: none; }
.burst-stat__tag { flex: none; font-weight: 700; }
.burst-stat b { margin-left: auto; flex: none; font-weight: 700; }
.burst-stat b.is-up { color: #f25c54; }
.burst-stat b.is-down { color: #16a34a; }
.burst-stat__empty { color: #909399; font-size: 12px; padding: 4px; }
.chart-card {
  background: #fff;
  border: 1px solid #d7e6f5;
  border-radius: 8px;
  padding: 8px 8px 2px;
  margin-bottom: 10px;
}
.chart-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
.pool-observe {
  position: sticky;
  left: 0;
  z-index: 3;
  width: calc(100vw - 210px);
  max-width: 100%;
  margin-bottom: 2px;
}
.pool-observe__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 28px;
  margin-bottom: 6px;
  b { font-size: 14px; color: #1f4e79; }
  button {
    height: 24px;
    padding: 0 10px;
    border: 1px solid #d7e4f6;
    border-radius: 4px;
    background: #f7faff;
    color: #1f4e79;
    font-size: 12px;
    cursor: pointer;
  }
}
.pool-leader-band {
  display: flex;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: 8px;
  overflow-x: auto;
}
.pool-leader-band ::v-deep .leader-col {
  flex: 0 0 300px;
  width: 300px;
}
.pool-leader-band__go {
  justify-self: start;
  align-self: center;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: #409eff;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.chart-head-main {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.chart-thumb-row {
  position: sticky;
  left: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;
  padding: 0 4px 4px;
  background: #fff;
}
.pool-slot { height: 168px; flex: none; }
.pool-board {
  height: 168px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 6px 8px;
  padding: 0;
  box-sizing: border-box;
}
.pool-chip {
  box-sizing: border-box;
  height: 52px;
  min-width: 58px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid #d7e6f5;
  border-radius: 8px;
  background: #fff;
  color: #1f4e79;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: none;
  }
  b { color: #e10600; font-size: 14px; line-height: 1; }
}
.pool-chip.is-on { background: #ecf5ff; border-color: #409eff; }
.pool-chip.is-go {
  color: #fff;
  background: #409eff;
  border-color: #409eff;
  font-size: 13px;
}
.cycle-thumb {
  flex: none;
  width: 320px;
  height: 168px;
  display: flex;
  flex-direction: column;
  padding: 6px 8px 4px;
  border: 1px solid #409eff;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  b {
    font-size: 14px;
    color: #1f4e79;
    line-height: 1.3;
  }
  img {
    flex: 1;
    width: 100%;
    min-height: 0;
    object-fit: contain;
    object-position: left center;
  }
}
.chart-title {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  padding: 2px 6px 0;
  white-space: nowrap;
  i { color: #000; margin-left: 4px; }
}
.rank-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 2px 10px;
  padding: 2px 4px 0;
  button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: 0;
    background: transparent;
    padding: 0;
    cursor: pointer;
    color: #000;
    font-size: 13px;
    font-weight: 600;
  }
  button.is-off { opacity: 0.35; }
  i {
    width: 14px;
    height: 8px;
    border-radius: 2px;
  }
}
.rank-legend__no {
  min-width: 14px;
  text-align: right;
  font-weight: 700;
}
.chart--return { height: 54vh; min-height: 440px; }
.chart--height { height: 46vh; min-height: 380px; }
.chart--return.is-fixed { height: 614px; min-height: 614px; }
.chart--height.is-fixed { height: 460px; min-height: 460px; }
.finals-axis { padding-top: 8px; padding-bottom: 8px; }
.finals-axis__title {
  font-size: 14px;
  font-weight: 700;
  color: #000;
  padding: 0 6px 6px;
}
.finals-axis__track { position: relative; }
.finals-col {
  position: absolute;
  top: 0;
  min-height: 18px;
  text-align: center;
}
.finals-date {
  font-size: 12px;
  font-weight: 700;
  color: #000;
  line-height: 1.2;
  padding-bottom: 4px;
}
.finals-line {
  font-size: 11px;
  line-height: 1.35;
  color: #303133;
  word-break: break-all;
  padding: 0 2px 6px;
}
.finals-line b { color: #1f4e79; }
.finals-line.is-hit {
  margin: 2px 1px 6px;
  padding: 4px 5px;
  border-radius: 4px;
  box-shadow: 0 3px 0 rgba(15, 23, 42, 0.28);
  font-weight: 800;
}
.finals-line.is-hit b,
.finals-line.is-hit .finals-star { color: inherit; font-weight: 800; }
.finals-star { color: #e6a23c; font-weight: 700; }
.event-pop {
  position: absolute;
  right: 12px;
  top: 42px;
  width: 240px;
  background: #fff;
  border: 1px solid #d7e6f5;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(31, 78, 121, 0.12);
  padding: 10px 12px;
  font-size: 12px;
  color: #606266;
  line-height: 1.6;
  z-index: 3;
}
.event-pop__head {
  display: flex;
  justify-content: space-between;
  color: #303133;
  i { cursor: pointer; color: #909399; }
}
.event-pop__type { font-weight: 700; }
.event-pop__desc { color: #909399; margin-top: 4px; }
.event-pop__item,
.event-pop__back {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin-top: 6px;
  padding: 4px 0;
  border: 0;
  background: transparent;
  color: #303133;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}
.event-pop__item i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}
.event-pop__back { color: #409eff; }
</style>
